import { Badge, HStack, Spinner, Tag, TagLabel, Text, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import { useEffect, useMemo } from "react";
import { useFoundationV2Years, useGetOrganizationEntities } from "../../../hooks/query/issb";
import {
  defaultFoundationYear,
  groupFoundationYears,
} from "../../../utils/foundationYearChoice";

/**
 * Picks which Foundation Data v2 reporting years the AI may draft from.
 *
 * A YEAR picker, not a datapoint picker, and deliberately: the whole point of this source is that every
 * approved figure for a year reaches the model, so letting someone deselect individual datapoints would
 * reintroduce by hand exactly the silent gaps that not indexing them avoids.
 *
 * Separate from `FoundationSourceSelector`, which drives v1 and selects ISSB question ids from the
 * foundation ROUND. The two systems run in parallel and an org may be on either.
 */
export default function FoundationV2SourceSelector({ round, selectedYears, setSelectedYears }) {
  const { data: years, isLoading } = useFoundationV2Years();

  /*
    One row per YEAR, not per workstream. Since ESG-4003 a year holds three `foundation_data_year` rows, which
    rendered as three chips all labelled "2026" — and since both the label and the selection key are the year,
    clicking one selected all three. They were never three choices: `foundation_year_ids` carries years, and the
    agent-scoped read aggregates every workstream for a year deliberately.
  */
  // Counted for THIS round's entities, and approved figures only — what the AI actually receives (ESG-4019).
  const yearList = useMemo(() => groupFoundationYears(years, { round }), [years, round]);

  /*
    Whose figures: the AI service passes the round, and Java narrows to the round's entities when it has any
    (every entity otherwise). Said here because nothing else on this card would tell you a subsidiary's round
    is not seeing the parent's figures.
  */
  const { data: orgEntities } = useGetOrganizationEntities();
  const roundEntityNames = useMemo(() => {
    const ids = round?.entities ?? [];
    if (!ids.length) return [];
    const names = new Map((orgEntities ?? []).map((e) => [e.id, e.name]));
    (years ?? []).forEach((y) => {
      if (y.entityId && !names.has(y.entityId)) names.set(y.entityId, y.entityName);
    });
    return ids.map((id) => names.get(id)).filter(Boolean);
  }, [round, orgEntities, years]);

  // The rule itself lives in utils/foundationYearChoice.js with its own test: a round is a PERIOD and
  // foundation data is a single year, so which one to preselect is a judgement, and picking the wrong one
  // would quietly feed last year's approved figures into this year's answers.
  useEffect(() => {
    if (selectedYears.length > 0) return;
    const preferred = defaultFoundationYear(yearList, round);
    if (preferred != null) setSelectedYears([String(preferred)]);
  }, [yearList, round, selectedYears, setSelectedYears]);

  /*
    Chosen years that give this round nothing. Said outright, because the draft otherwise runs with no foundation
    figures and no foundation citations, and nothing anywhere explains it — the figures are usually there, filed
    under another entity (on staging, the ESG-4016 migration's organisation-named one).
  */
  const emptySelected = useMemo(
    () =>
      yearList
        .filter((y) => selectedYears.includes(String(y.year)) && y.curatedRowCount === 0)
        .map((y) => y.year),
    [yearList, selectedYears]
  );

  const toggle = (year) => {
    const value = String(year);
    setSelectedYears(
      selectedYears.includes(value)
        ? selectedYears.filter((y) => y !== value)
        : [...selectedYears, value]
    );
  };

  if (isLoading) return <Spinner size="sm" />;

  if (yearList.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500">
        No Foundation Data years yet. Add one on the Foundation Data page and extract or approve some
        figures first.
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={2}>
      {/* Not "every approved figure is given to the AI": the prompt holds as many as fit its budget (an AI
          setting), and the AI looks the rest up with foundation_data_query. The old wording was untrue for any
          year past ~49 figures, and silently so. */}
      <Text fontSize="xs" color="gray.500">
        Approved figures for the years you pick are given to the AI and take priority over a source document
        that says something different. If a year has more than fit, the AI can look up the rest.
      </Text>
      <Text fontSize="xs" color="gray.500">
        {roundEntityNames.length
          ? `Figures from this round's entities: ${roundEntityNames.join(", ")}.`
          : "Figures from all entities."}
      </Text>
      {emptySelected.length > 0 ? (
        <Text fontSize="xs" color="orange.600">
          {emptySelected.join(", ")} {emptySelected.length === 1 ? "has" : "have"} no approved figures
          {roundEntityNames.length ? ` for ${roundEntityNames.join(", ")}` : ""}, so the AI will draft without
          foundation data.
          {roundEntityNames.length
            ? " If the figures exist, check on the Foundation Data page that they are filed under this round's entity."
            : " Approve some figures on the Foundation Data page first."}
        </Text>
      ) : null}
      <Wrap spacing={2}>
        {yearList.map((year) => {
          const isSelected = selectedYears.includes(String(year.year));
          return (
            <WrapItem key={year.year}>
              <VStack align="stretch" spacing={1}>
                <Tag
                  size="md"
                  borderRadius="full"
                  variant={isSelected ? "solid" : "outline"}
                  colorScheme={isSelected ? "green" : "gray"}
                  cursor="pointer"
                  onClick={() => toggle(year.year)}
                >
                  <HStack spacing={2}>
                    <TagLabel>{year.year}</TagLabel>
                    {/* The TOTAL across workstreams — what picking this year actually contributes. */}
                    <Badge
                      variant="subtle"
                      colorScheme={isSelected ? "green" : "gray"}
                      fontSize="10px"
                    >
                      {year.curatedRowCount}
                    </Badge>
                  </HStack>
                </Tag>
                {/*
                  Where the figures come from, under the chip rather than on it. A workstream with none is
                  still listed: "Targets 0" answers "why did the targets questions get nothing", and an absent
                  line does not. Not clickable — the year is the unit of selection.
                */}
                {year.workstreams.length > 1 && (
                  <VStack align="start" spacing={0} pl={3}>
                    {year.workstreams.map((workstream) => (
                      <Text
                        key={workstream.questionGroup}
                        fontSize="10px"
                        color={workstream.curatedRowCount > 0 ? "gray.600" : "gray.400"}
                      >
                        {workstream.questionGroup} {workstream.curatedRowCount}
                      </Text>
                    ))}
                  </VStack>
                )}
              </VStack>
            </WrapItem>
          );
        })}
      </Wrap>
    </VStack>
  );
}
